import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { QRCodeCanvas } from 'qrcode.react'

// ---------------------------------------------------------------------------
// API response type for GET /api/external/parking/:token
// ---------------------------------------------------------------------------
interface ParkingSession {
  session_id: number
  state: 'check_in' | 'pay' | 'paid' | 'rejected' | 'overstay_contact_admin'
  license_plate: string | null
  entered_at: string | null
  exited_at: string | null
  charge_amount: number | null
  /** Bare storage key — render via /api/images/<key> */
  invoice_qr_image: string | null
  /** Dynamic PromptPay QR payload (EMVCo string). When present, render client-side QR instead of invoice_qr_image. */
  promptpay_payload: string | null
  /** PromptPay reference number to display beneath the dynamic QR. */
  promptpay_ref1: string | null
  /** Set once a slip has been submitted: drives the waiting view across reloads. */
  slip_uploaded_at?: string | null
  /** The operator's reason when state is `rejected` (null when none was given). */
  reject_reason?: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

/** Bare storage key → renderable image URL */
function imageUrl(key: string): string {
  return `${API_BASE_URL}/api/images/${key}`
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(amount)
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  } catch {
    return ''
  }
}

async function fetchSession(token: string): Promise<ParkingSession> {
  const res = await axios.get<ParkingSession>(`${API_BASE_URL}/api/external/parking/${token}`)
  return res.data
}

/** Get a presigned PUT URL for a slip image (token-scoped, no OAuth). */
async function getPresignedUrl(token: string, contentType: string): Promise<{ url: string; key: string }> {
  const res = await axios.post<{ url: string; key: string }>(
    `${API_BASE_URL}/api/external/parking/${token}/presigned_url`,
    { content_type: contentType },
  )
  return res.data
}

/** The code of a flat `{ error: "<code>" }` 422 body, the parking API's error shape. */
function errorCode(err: unknown): string | null {
  const axiosErr = err as { response?: { data?: { error?: unknown } } }
  const code = axiosErr?.response?.data?.error
  return typeof code === 'string' ? code : null
}

/** Upload slip image to storage and submit key to server. */
async function submitSlip(token: string, file: File): Promise<void> {
  // 1. Get a presigned PUT URL (bare UUID key) from the server.
  const { url, key } = await getPresignedUrl(token, file.type)

  // 2. PUT the file directly to storage — no app auth headers on a presigned URL.
  const putRes = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  if (!putRes.ok) {
    throw new Error(`upload failed: ${putRes.status}`)
  }

  // 3. Record the bare key on the payment record.
  await axios.post(`${API_BASE_URL}/api/external/parking/${token}/slip_upload`, {
    slip_image_key: key,
  })
}

// ---------------------------------------------------------------------------
// DynamicQR — renders a client-side PromptPay QR from the EMVCo payload.
// Falls back to null on any render error so the caller can show the static img.
// ---------------------------------------------------------------------------

interface DynamicQRProps {
  payload: string
  ariaLabel: string
}

function DynamicQR({ payload, ariaLabel }: DynamicQRProps) {
  // Basic payload validity guard — EMVCo strings are at least 20 chars.
  if (!payload || payload.trim().length < 20) return null

  // No try/catch: React defers rendering, so a render error here would not be
  // caught synchronously (react-hooks/error-boundaries). The length guard above
  // is the real safeguard; wrap the tree in an error boundary if that changes.
  return (
    <QRCodeCanvas
      value={payload}
      size={192}
      className="w-48 h-48 mx-auto"
      aria-label={ariaLabel}
    />
  )
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

interface CheckInViewProps {
  session: ParkingSession
  onRefresh: () => void
  isRefreshing: boolean
}

function CheckInView({ session, onRefresh, isRefreshing }: CheckInViewProps) {
  const { t } = useTranslation('external_parking')

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8 text-center">
      {/* Clock icon */}
      <div className="text-6xl" aria-hidden="true">🕐</div>
      <div className="space-y-1">
        <p className="text-lg font-semibold">
          {t('checked_in_at', { time: formatTime(session.entered_at) })}
        </p>
        <p className="text-base-content/60 text-sm">{t('checked_in_subtitle')}</p>
        {session.license_plate && (
          <p className="font-mono text-sm text-base-content/70">{session.license_plate}</p>
        )}
      </div>

      {/*
       * "Tap to pay now" re-fetches the session state. The actual check-out
       * is triggered by the gate/kiosk (HMAC-gated endpoint); this button
       * simply refreshes so the UI reflects the new state once the vehicle
       * has exited and the operator has triggered check-out.
       */}
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label={t('tap_to_refresh')}
      >
        {isRefreshing
          ? <span className="loading loading-spinner loading-xs" />
          : t('tap_to_refresh')}
      </button>
    </div>
  )
}

interface PayViewProps {
  session: ParkingSession
  token: string
  onSubmitted: () => void
  onContactStaff: () => void
  onAlreadyPaid: () => void
}

function PayView({ session, token, onSubmitted, onContactStaff, onAlreadyPaid }: PayViewProps) {
  const { t } = useTranslation('external_parking')
  const { t: tc } = useTranslation('common')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [slipFile, setSlipFile] = useState<File | null>(null)
  const [slipPreview, setSlipPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setSlipFile(file)
    const reader = new FileReader()
    reader.onload = () => setSlipPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async () => {
    if (!slipFile) {
      setUploadError(t('errors.no_file'))
      return
    }
    setIsUploading(true)
    setUploadError(null)
    try {
      await submitSlip(token, slipFile)
      // EP-161 §2.2: a person verifies every parking slip, so the page waits
      // and polls; "paid" comes only from the server (EP-162 7.6).
      onSubmitted()
    } catch (err: unknown) {
      const code = errorCode(err)
      if (code === 'contact_staff') {
        // The session was marked as fraud: the visitor is told only to see
        // the staff, never why, and gets no retry.
        onContactStaff()
      } else if (code === 'already_paid') {
        // Someone verified or settled it meanwhile: the refetch shows paid.
        onAlreadyPaid()
      } else {
        setUploadError(t('errors.upload_failed'))
      }
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="space-y-5 px-4 py-6">
      {/* Charge amount */}
      {session.charge_amount != null && (
        <div className="text-center">
          <p className="text-2xl font-bold text-primary">
            {t('charge_amount', { amount: formatCurrency(session.charge_amount) })}
          </p>
        </div>
      )}

      {/* QR code for payment — dynamic PromptPay [A] or static operator image [B] */}
      {session.promptpay_payload ? (
        <div className="text-center space-y-2">
          <p className="text-sm font-medium text-base-content/80">{t('scan_qr_to_pay')}</p>
          <DynamicQR
            payload={session.promptpay_payload}
            ariaLabel={t('scan_dynamic_qr')}
          />
          {session.promptpay_ref1 && (
            <p className="font-mono text-sm text-center text-base-content/70">
              {t('reference_label')}: {session.promptpay_ref1}
            </p>
          )}
        </div>
      ) : session.invoice_qr_image ? (
        <div className="text-center space-y-2">
          <p className="text-sm font-medium text-base-content/80">{t('scan_qr_to_pay')}</p>
          <img
            src={imageUrl(session.invoice_qr_image)}
            alt={t('scan_qr_to_pay')}
            className="mx-auto w-48 h-48 object-contain"
          />
        </div>
      ) : null}

      {/* Slip upload */}
      <div className="space-y-3">
        <p className="text-sm font-medium">{t('upload_slip')}</p>

        {slipPreview ? (
          <div className="relative">
            <img
              src={slipPreview}
              alt={t('upload_slip')}
              className="w-full rounded-lg max-h-64 object-contain bg-base-200"
            />
            <button
              type="button"
              className="btn btn-circle btn-sm btn-ghost absolute top-2 right-2"
              onClick={() => { setSlipFile(null); setSlipPreview(null) }}
            >
              ✕
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-base-300 rounded-lg cursor-pointer hover:border-primary">
            <span className="text-sm text-base-content/60">{t('tap_to_upload')}</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
              aria-label={t('tap_to_upload')}
            />
          </label>
        )}

        {uploadError && (
          <div className="alert alert-error text-sm" role="alert">
            <span>{uploadError}</span>
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary w-full"
          disabled={!slipFile || isUploading}
          onClick={() => { void handleSubmit() }}
        >
          {isUploading
            ? <span className="loading loading-spinner loading-sm" />
            : t('submit_slip')}
        </button>

        <p className="text-xs text-base-content/50 text-center">
          {tc('errors.something_went_wrong')}
        </p>
      </div>
    </div>
  )
}

interface ResubmitViewProps {
  session: ParkingSession
  onResubmit: () => void
}

// Slip submitted. A person verifies every parking slip (the ML reading is for
// reference only). The page polls every 15 s and shows paid once the server
// reports it, or the rejection when an operator rejects the slip.
function WaitingView({ session, onResubmit }: ResubmitViewProps) {
  const { t } = useTranslation('external_parking')

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8 px-4 text-center">
      <div
        className="w-20 h-20 rounded-full bg-warning/20 flex items-center justify-center text-4xl"
        aria-hidden="true"
      >
        🕐
      </div>
      <div className="space-y-1">
        <p className="text-xl font-bold">{t('waiting_title')}</p>
        <p className="text-base-content/60 text-sm">{t('waiting_subtitle')}</p>
        {session.license_plate && (
          <p className="font-mono text-sm text-base-content/70">{session.license_plate}</p>
        )}
      </div>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onResubmit}>
        {t('upload_new_slip')}
      </button>
    </div>
  )
}

// An operator rejected the slip: the reason when one was given, and a way to
// send a new slip.
function RejectedView({ session, onResubmit }: ResubmitViewProps) {
  const { t } = useTranslation('external_parking')

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8 px-4 text-center">
      <div
        className="w-20 h-20 rounded-full bg-error/20 text-error flex items-center justify-center text-4xl font-bold"
        aria-hidden="true"
      >
        !
      </div>
      <div className="space-y-1">
        <p className="text-xl font-bold">{t('rejected_title')}</p>
        {session.reject_reason && (
          <p className="text-sm">{t('rejected_reason', { reason: session.reject_reason })}</p>
        )}
        <p className="text-sm text-base-content/70">
          {session.license_plate && (
            <span className="font-mono">{t('rejected_plate', { plate: session.license_plate })}</span>
          )}
          {session.license_plate && session.charge_amount != null && ' · '}
          {session.charge_amount != null && `฿${formatCurrency(session.charge_amount)}`}
        </p>
      </div>
      <div className="w-full max-w-sm space-y-2">
        <button type="button" className="btn btn-primary w-full" onClick={onResubmit}>
          {t('rejected_resubmit')}
        </button>
        <p className="text-sm text-base-content/60">{t('rejected_contact_staff')}</p>
      </div>
    </div>
  )
}

// No button: the visitor cannot pay from this page, a staff member settles it.
// Used for slip_upload's contact_staff answer and for the overstay state.
function StaffOnlyView({ session, title, subtitle }: { session: ParkingSession; title: string; subtitle: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8 px-4 text-center">
      <div
        className="w-20 h-20 rounded-full bg-info/20 text-info flex items-center justify-center text-4xl font-bold"
        aria-hidden="true"
      >
        i
      </div>
      <div className="space-y-1">
        <p className="text-lg font-semibold">{title}</p>
        <p className="text-base-content/60 text-sm">{subtitle}</p>
        {session.license_plate && (
          <p className="font-mono text-sm text-base-content/70">{session.license_plate}</p>
        )}
      </div>
    </div>
  )
}

interface PaidViewProps {
  session: ParkingSession
}

function PaidView({ session }: PaidViewProps) {
  const { t } = useTranslation('external_parking')

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8 text-center">
      <div
        className="w-20 h-20 rounded-full bg-success flex items-center justify-center text-success-content text-4xl font-bold"
        aria-hidden="true"
      >
        ✓
      </div>
      <div className="space-y-1">
        <p className="text-xl font-bold text-success">{t('paid_title')}</p>
        <p className="text-base-content/60 text-sm">
          {t('paid_subtitle', { time: formatTime(session.exited_at) })}
        </p>
        {session.license_plate && (
          <p className="font-mono text-sm text-base-content/70">{session.license_plate}</p>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ExternalParkingPage() {
  const { t } = useTranslation('external_parking')
  const { t: tc } = useTranslation('common')
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  // When a slip was submitted this page-load (Date.now()), so the waiting view
  // shows before the next poll lands; whether the visitor asked to send a new
  // slip; and whether slip_upload answered contact_staff (kept for the
  // page-load whatever the poll returns).
  const [submittedAt, setSubmittedAt] = useState<number | null>(null)
  const [resubmitting, setResubmitting] = useState(false)
  const [contactStaff, setContactStaff] = useState(false)

  const {
    data: session,
    dataUpdatedAt,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<ParkingSession, Error>({
    queryKey: ['external_parking', token],
    queryFn: () => fetchSession(token),
    enabled: token.length > 0,
    // A slip waits for a person: poll every 15 s so the page shows paid or
    // rejected promptly after an operator decides; otherwise every 60 s.
    refetchInterval: (query) => {
      const s = query.state.data
      return s?.state === 'pay' && s.slip_uploaded_at ? 15_000 : 60_000
    },
    retry: false,
  })

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="alert alert-error max-w-sm">
          <span>{t('errors.invalid_token')}</span>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4 max-w-sm w-full">
          <div className="alert alert-error">
            <span>{t('errors.load_failed')}</span>
          </div>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => { void refetch() }}
          >
            {tc('actions.retry')}
          </button>
        </div>
      </div>
    )
  }

  // Server state is authoritative: "paid" only after an operator verifies
  // (EP-162 7.6). A slip on the server shows the waiting view; a slip sent
  // from this page shows it too until a fetch newer than the submit lands,
  // so a re-upload on a rejected payment does not flash the old rejection.
  const awaitingFreshState = submittedAt !== null && (dataUpdatedAt ?? 0) < submittedAt
  const waiting =
    !resubmitting &&
    ((session.state === 'pay' && (!!session.slip_uploaded_at || awaitingFreshState)) ||
      (session.state === 'rejected' && awaitingFreshState))

  return (
    <div className="min-h-screen bg-base-200">
      {/* Header */}
      <div className="bg-base-100 px-4 py-3 flex items-center justify-between shadow-sm">
        <h1 className="text-base font-bold">{t('title')}</h1>
        {/* Background-refresh indicator */}
        {isFetching && !isLoading && (
          <span
            className="loading loading-spinner loading-xs text-primary"
            aria-label={tc('status.loading')}
          />
        )}
      </div>

      {/* Body */}
      <div className="max-w-sm mx-auto w-full">
        {session.state === 'check_in' && (
          <CheckInView
            session={session}
            onRefresh={() => { void refetch() }}
            isRefreshing={isFetching}
          />
        )}

        {(session.state === 'pay' || session.state === 'rejected') && (
          contactStaff ? (
            <StaffOnlyView
              session={session}
              title={t('contact_staff_title')}
              subtitle={t('contact_staff_subtitle')}
            />
          ) : waiting ? (
            <WaitingView session={session} onResubmit={() => setResubmitting(true)} />
          ) : session.state === 'rejected' && !resubmitting ? (
            <RejectedView session={session} onResubmit={() => setResubmitting(true)} />
          ) : (
            <PayView
              session={session}
              token={token}
              onSubmitted={() => {
                setSubmittedAt(Date.now())
                setResubmitting(false)
                void refetch()
              }}
              onContactStaff={() => {
                setContactStaff(true)
                setResubmitting(false)
              }}
              onAlreadyPaid={() => {
                setResubmitting(false)
                void refetch()
              }}
            />
          )
        )}

        {session.state === 'paid' && <PaidView session={session} />}

        {session.state === 'overstay_contact_admin' && (
          <StaffOnlyView
            session={session}
            title={t('overstay_title')}
            subtitle={t('overstay_subtitle')}
          />
        )}
      </div>
    </div>
  )
}
